import { Box, Typography } from '@mui/joy';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { DocumentCard as BaseDocumentCard } from 'core/components';
import { TextEllipsis } from 'core/elements';
import { QuoteEntityType, QuoteType } from 'quotes/enums';
import { IQuoteFile } from 'quotes/types';
import { Color, Level } from 'theme/enums';

import { QuoteFileSource } from './enums';
import { getQuoteFileSourceLabel, getQuoteFileUserName } from './utils';

interface IProps {
  quoteFile: IQuoteFile;
  entityType: QuoteEntityType;
  quoteType: QuoteType;
  onRemoveFile: (fileId: number) => Promise<void>;
}

export const DocumentCard = ({ quoteFile, entityType, quoteType, onRemoveFile }: IProps) => {
  const { t } = useTranslation();

  const getSourceLabel = (quoteFile: IQuoteFile) => {
    switch (quoteFile.source) {
      case QuoteFileSource.BOM_IMPORT:
      case QuoteFileSource.VENDOR_QUOTE_IMPORT:
      case QuoteFileSource.UPLOAD:
        return t('labelUploaded');
      case QuoteFileSource.DOWNLOAD:
        return t('labelDownloaded');
      case QuoteFileSource.SPA_SENT:
        return t('labelSentAsSpa');
    }
  };

  const renderInfoRow = (label: string, content: string) => (
    <Box>
      <Typography level={Level.BODY_XS}>{label}:</Typography>
      <TextEllipsis content={content} maxRows={2} />
    </Box>
  );

  const renderExtraInfo = (quoteFile: IQuoteFile) => {
    if (QuoteFileSource.SPA_SENT === quoteFile.source) {
      return renderInfoRow(t('labelSentBy'), getQuoteFileUserName(quoteFile));
    }

    return renderInfoRow(
      t('labelSource'),
      getQuoteFileSourceLabel(entityType, quoteType)[quoteFile.source]
    );
  };

  // Imported and sent files are the record of what happened; only the BE writes or drops them.
  const isFileRemovable = useMemo(
    () =>
      ![QuoteFileSource.VENDOR_QUOTE_IMPORT, QuoteFileSource.SPA_SENT].includes(quoteFile.source),
    [quoteFile.source]
  );

  const sourceLabelColor = useMemo(() => {
    return quoteFile.source === QuoteFileSource.DOWNLOAD ? Color.SUCCESS : Color.PRIMARY;
  }, [quoteFile.source]);

  return (
    <BaseDocumentCard
      file={quoteFile}
      onRemove={isFileRemovable ? onRemoveFile : undefined}
      extraInfo={renderExtraInfo(quoteFile)}
      sourceLabel={getSourceLabel(quoteFile)}
      sourceLabelColor={sourceLabelColor}
    />
  );
};
